// Fresh 3D Visualization Engine - CP1A/CP1B Checkpoint Scene
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useGLTF, OrbitControls, Environment } from '@react-three/drei';
import * as THREE from 'three';

interface Fresh3DEngineProps {
  modelPath: string;
  occupancyData: Record<string, number> | null;
  flowData?: Record<string, number> | null;
  thresholds?: Record<string, { green: number; yellow: number; amber: number; minflow?: number; maxflow?: number }> | { green: number; yellow: number; amber: number };
  checkpointId: string;
  className?: string;
}

interface HeatmapZoneProps {
  object: THREE.Object3D;
  occupancyData: Record<string, number> | null;
  thresholds: Record<string, { green: number; yellow: number; amber: number; minflow?: number; maxflow?: number }> | { green: number; yellow: number; amber: number };
}

// Material cache for reusing materials
const materialCache = new Map<string, THREE.MeshStandardMaterial>();

// Get material based on occupancy level - matching 2D colors exactly
const getHeatmapMaterial = (occupancy: number | undefined, thresholds: { green: number; yellow: number; amber: number }): THREE.MeshStandardMaterial => {
  let colorKey = 'default';
  let color = new THREE.Color(0.6, 0.6, 0.6); // Grey: rgb(153, 153, 153) = #999999
  let opacity = 0.2; // Match 2D opacity exactly

  if (occupancy !== undefined && occupancy > 0) {
    if (occupancy === 0) {
      colorKey = 'grey';
      color = new THREE.Color(0.42, 0.45, 0.51); // Grey: rgb(107, 114, 128) = #6B7280
      opacity = 0.2; // Lighter opacity for zero occupancy
    } else if (occupancy <= thresholds.green) {
      colorKey = 'green';
      color = new THREE.Color(0.133, 0.773, 0.369); // Green: rgb(34, 197, 94) = #22C55E
      opacity = 0.35;
    } else if (occupancy <= thresholds.yellow) {
      colorKey = 'yellow';
      color = new THREE.Color(0.918, 0.702, 0.031); // Yellow: rgb(234, 179, 8) = #EAB308
      opacity = 0.35;
    } else if (occupancy <= thresholds.amber) {
      colorKey = 'amber';
      color = new THREE.Color(0.961, 0.396, 0.396); // Amber: rgb(245, 101, 101) = #F56565
      opacity = 0.35;
    } else {
      colorKey = 'red';
      color = new THREE.Color(0.937, 0.267, 0.267); // Red: rgb(239, 68, 68) = #EF4444
      opacity = 0.4; // Red has slightly higher opacity in 2D
    }
  } else {
    // No occupancy data - still show green
    colorKey = 'default';
    color = new THREE.Color(0.6, 0.6, 0.6); // Grey: rgb(153, 153, 153) = #999999
    opacity = 0.2;
  }
  
  // Always create a new material to ensure proper color application
  const material = new THREE.MeshStandardMaterial({
    color: color,
    transparent: true,
    opacity: opacity,
    side: THREE.DoubleSide,
    depthWrite: true,
    emissive: color,
    emissiveIntensity: 0.05, // Reduced for exact 2D match
  });
  
  return material;
};

// Component for individual heatmap zones with hover functionality
const HeatmapZone: React.FC<HeatmapZoneProps & { 
  onHover?: (assetCode: string | null, occupancy: number | null) => void 
}> = ({ object, occupancyData, thresholds, onHover }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [isHovered, setIsHovered] = useState(false);
  
  // Extract asset code
  const assetCode = useMemo(() => {
    return object.userData?.assetcode || 
           object.userData?.extras?.assetcode ||
           (object.name.startsWith("Heatmap_") ? object.name.replace("Heatmap_", "") : null);
  }, [object]);
  
  useEffect(() => {
    if (!object.name || !assetCode) return;
    
    // Get occupancy data for this zone
    const occupancy = occupancyData?.[assetCode];
    
    // Extract the basic thresholds for heatmap (only need green, yellow, amber)
    const basicThresholds = (typeof thresholds === 'object' && 'green' in thresholds && !Array.isArray(thresholds))
      ? { green: thresholds.green, yellow: thresholds.yellow, amber: thresholds.amber }
      : (thresholds as Record<string, any>)?.[assetCode] || { green: 20, yellow: 25, amber: 30 };
    
    // Get appropriate material with hover effect
    let material = getHeatmapMaterial(occupancy, basicThresholds);
    
    // If hovered, make it brighter
    if (isHovered) {
      const hoveredMaterial = material.clone();
      hoveredMaterial.emissiveIntensity = 0.3; // Brighter when hovered
      hoveredMaterial.opacity = 0.6; // More opaque when hovered
      material = hoveredMaterial;
    }
    
    // Apply material to mesh
    const mesh = object as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.material = material;
    }
    
  }, [object, occupancyData, thresholds, assetCode, isHovered]);
  
  const handlePointerOver = () => {
    setIsHovered(true);
    const occupancy = occupancyData?.[assetCode || ''];
    if (onHover && assetCode) {
      onHover(assetCode, occupancy ?? null);
    }
    document.body.style.cursor = 'pointer';
  };
  
  const handlePointerOut = () => {
    setIsHovered(false);
    if (onHover) {
      onHover(null, null);
    }
    document.body.style.cursor = 'auto';
  };
  
  return (
    <primitive 
      ref={meshRef}
      object={object}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    />
  );
};

// Passenger Flow Animation Component
const PassengerFlowAnimation: React.FC<{
  heatmapObjects: THREE.Object3D[],
  occupancyData: Record<string, number> | null,
  flowData: Record<string, number> | null | undefined,
  thresholds: Record<string, { green: number; yellow: number; amber: number; minflow?: number; maxflow?: number }> | { green: number; yellow: number; amber: number },
  enabled: boolean
}> = ({ heatmapObjects, occupancyData, flowData, thresholds, enabled }) => {
  const groupRef = useRef<THREE.Group>(null);
  const [animatedCylinders, setAnimatedCylinders] = useState<THREE.Mesh[]>([]);
  
  
  // Get cylinder color based on FLOW RATE (matching 2D logic exactly)
  const getCylinderColor = (flowOut: number, assetThresholds: any): number => {
    const minFlow = assetThresholds?.minflow || 5;
    const maxFlow = assetThresholds?.maxflow || 20;
    
    // Calculate flow boundaries (same as 2D)
    const avgLowerBound = 1.3 * minFlow;
    const avgUpperBound = 0.75 * maxFlow;
    
    // Determine color based on flow rate (matching 2D)
    if (flowOut < avgLowerBound) {
      return 0xef4444; // Red for slow flow (congested)
    } else if (flowOut <= avgUpperBound) {
      return 0xeab308; // Yellow for average flow
    } else {
      return 0x22c55e; // Green for fast flow
    }
  };
  
  // Calculate animation speed based on flow rate (matching 2D)
  const getAnimationSpeed = (flowOut: number, assetThresholds: any): number => {
    const minFlow = assetThresholds?.minflow || 5;
    const maxFlow = assetThresholds?.maxflow || 20;
    
    const avgLowerBound = 1.3 * minFlow;
    const avgUpperBound = 0.75 * maxFlow;
    
    if (flowOut < avgLowerBound) {
      return 0.001; // Slow speed for congested flow
    } else if (flowOut > avgUpperBound) {
      return 0.0025; // Fast speed for smooth flow
    } else {
      return 0.0015; // Normal speed for average flow
    }
  };

  // Parse passenger path coordinates from GLTF metadata
  const parsePassengerPath = (pathData: string, checkpointId: string): THREE.Vector3[] => {
    if (!pathData) return [];
    
    const points = pathData.split('|').map((point: string) => {
      const [x, y] = point.split(',').map(Number);
      
      // The GLTF conversion flips the model
      // We need to negate Z to match the flipped model orientation
      return new THREE.Vector3(
        x,          // X stays the same
        0.1,        // Height at floor level
        -y          // Negate Y to match the flipped model
      );
    });
    
    return points;
  };

  // Calculate total path length
  const calculatePathLength = (pathPoints: THREE.Vector3[]): number => {
    let totalLength = 0;
    for (let i = 1; i < pathPoints.length; i++) {
      const distance = pathPoints[i].distanceTo(pathPoints[i - 1]);
      totalLength += distance;
    }
    return totalLength;
  };
  
  // Get position along path at a specific progress (0 to 1)
  const getPositionAlongPath = (pathPoints: THREE.Vector3[], progress: number): THREE.Vector3 => {
    if (pathPoints.length === 0) return new THREE.Vector3();
    if (progress <= 0) return pathPoints[0].clone();
    if (progress >= 1) return pathPoints[pathPoints.length - 1].clone();
    
    const totalLength = calculatePathLength(pathPoints);
    const targetDistance = totalLength * progress;
    
    let accumulatedDistance = 0;
    for (let i = 1; i < pathPoints.length; i++) {
      const segmentLength = pathPoints[i].distanceTo(pathPoints[i - 1]);
      
      if (accumulatedDistance + segmentLength >= targetDistance) {
        const segmentProgress = (targetDistance - accumulatedDistance) / segmentLength;
        const position = new THREE.Vector3();
        position.lerpVectors(pathPoints[i - 1], pathPoints[i], segmentProgress);
        return position;
      }
      
      accumulatedDistance += segmentLength;
    }
    
    return pathPoints[pathPoints.length - 1].clone();
  };

  // Create animated cylinder with initial position
  const createAnimatedCylinder = (
    initialProgress: number,
    pathPoints: THREE.Vector3[],
    color: number,
    speed: number,
    cylinderIndex: number,
    totalCylinders: number
  ): THREE.Mesh => {
    const geometry = new THREE.CylinderGeometry(0.15, 0.15, 0.6, 8);
    const material = new THREE.MeshStandardMaterial({
      color: color,
      transparent: true,
      opacity: 0.8
    });
    
    const cylinder = new THREE.Mesh(geometry, material);
    
    // Start cylinders evenly distributed along the path
    (cylinder as any).animationData = {
      pathPoints,
      progress: initialProgress,
      speed: speed,
      cylinderIndex: cylinderIndex,
      totalCylinders: totalCylinders
    };
    
    // Set initial position
    const startPosition = getPositionAlongPath(pathPoints, initialProgress);
    cylinder.position.copy(startPosition);
    
    return cylinder;
  };

  // Animation loop - continuous belt movement
  useFrame(() => {
    if (!enabled || !groupRef.current) return;
    
    animatedCylinders.forEach((cylinder) => {
      const animData = (cylinder as any).animationData;
      if (!animData) return;
      
      // Update progress continuously
      animData.progress += animData.speed;
      
      // Wrap around when reaching the end (continuous belt)
      if (animData.progress >= 1) {
        animData.progress = animData.progress % 1;
      }
      
      // Calculate position along path
      const position = getPositionAlongPath(animData.pathPoints, animData.progress);
      cylinder.position.copy(position);
      
      // Add slight random offset for visual variation (smaller than before)
      cylinder.position.x += (Math.random() - 0.5) * 0.05;
      cylinder.position.z += (Math.random() - 0.5) * 0.05;
    });
  });

  // Generate cylinders when data changes
  useEffect(() => {
    if (!enabled || !groupRef.current || !flowData || Object.keys(flowData).length === 0) {
      if (groupRef.current) {
        animatedCylinders.forEach(cylinder => {
          groupRef.current?.remove(cylinder);
        });
        setAnimatedCylinders([]);
      }
      return;
    }
    
    // Clear existing cylinders
    animatedCylinders.forEach(cylinder => {
      groupRef.current?.remove(cylinder);
    });
    
    const newCylinders: THREE.Mesh[] = [];
    
    heatmapObjects.forEach(heatmapObj => {
      const assetCode = heatmapObj.userData?.assetcode || 
                       heatmapObj.userData?.extras?.assetcode;
      if (!assetCode) return;
      
      const flowOut = flowData[assetCode];
      if (!flowOut || flowOut <= 0) return;
      
      // Get passenger path data from GLTF metadata
      const pathData = heatmapObj.userData?.passenger_path_data ||
                      heatmapObj.userData?.extras?.passenger_path_data;
      
      if (!pathData) {
        return;
      }
      
      // Detect checkpoint type from heatmap name
      const checkpointType = heatmapObj.name.includes('t1-1b') ? 'CP-1B' : 'CP-1A';
      const pathPoints = parsePassengerPath(pathData, checkpointType);
      if (pathPoints.length <= 1) return;
      
      // Get thresholds for this asset
      const assetThresholds = (typeof thresholds === 'object' && 'green' in thresholds && !('minflow' in thresholds)) 
        ? thresholds 
        : (thresholds as Record<string, any>)?.[assetCode] || { green: 20, yellow: 25, amber: 30 };
      
      // Get color based on FLOW RATE (not occupancy)
      const cylinderColor = getCylinderColor(flowOut, assetThresholds);
      
      // Get animation speed based on flow rate
      const animationSpeed = getAnimationSpeed(flowOut, assetThresholds);
      
      // Calculate path length and cylinder spacing
      const pathLength = calculatePathLength(pathPoints);
      const cylinderSpacing = 0.9; // Same as 2D dot spacing
      const maxCylinders = Math.floor(pathLength / cylinderSpacing);
      const cylinderCount = Math.min(flowOut, maxCylinders); // Limit by path space
      
      // Create cylinders evenly distributed along the path
      for (let i = 0; i < cylinderCount; i++) {
        const initialProgress = i / cylinderCount; // Evenly distribute from 0 to 1
        const cylinder = createAnimatedCylinder(
          initialProgress,
          pathPoints,
          cylinderColor,
          animationSpeed,
          i,
          cylinderCount
        );
        
        groupRef.current?.add(cylinder);
        newCylinders.push(cylinder);
      }
    });
    
    setAnimatedCylinders(newCylinders);
  }, [enabled, heatmapObjects, flowData, thresholds]);

  return <group ref={groupRef} />;
};
// Component for showing passenger path on hover
const HoveredPathVisualization: React.FC<{
  assetCode: string | null,
  checkpointId: string,
  pathData?: any // Will come from config
}> = ({ assetCode, checkpointId, pathData }) => {
  const lineRef = useRef<THREE.Line>(null);
  
  if (!assetCode || !pathData) return null;
  
  // Get the passenger path for this asset
  const assetPathData = pathData?.[assetCode]?.passengerPath;
  if (!assetPathData || assetPathData.length === 0) return null;
  
  // Convert path coordinates to 3D positions
  const points = assetPathData.map((coord: {x: number, y: number}) => {
    if (checkpointId === 'CP-1B' || checkpointId === 'cp-1b') {
      return new THREE.Vector3(
        (coord.x - 14.5) * 0.8,  // Adjust for CP-1B centering
        0.8,                      // Slightly above floor
        (coord.y - 22.5) * 0.8   // Adjust for CP-1B centering
      );
    } else {
      // CP-1A adjustment
      return new THREE.Vector3(
        coord.y - 37,    // Swap and offset for CP-1A
        0.8,             // Height above floor
        coord.x - 30     // Swap and offset for CP-1A
      );
    }
  });
  
  // Create line geometry
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineBasicMaterial({ color: 0x60a5fa, linewidth: 3 });
  
  // Create arrow at the end (exit point)
  const lastPoint = points[points.length - 1];
  const secondLastPoint = points[points.length - 2] || points[points.length - 1];
  
  // Calculate arrow direction
  const direction = new THREE.Vector3().subVectors(lastPoint, secondLastPoint).normalize();
  
  return (
    <group>
      {/* Path line using primitive */}
      <primitive object={new THREE.Line(geometry, material)} />
      
      {/* Exit arrow */}
      <mesh position={lastPoint}>
        <coneGeometry args={[0.3, 0.8, 4]} />
        <meshBasicMaterial color={0x60a5fa} />
      </mesh>
    </group>
  );
};

// Main 3D scene component with hover support
const Scene3D: React.FC<Omit<Fresh3DEngineProps, 'className'> & { 
  showPassengerFlow: boolean,
  onHover?: (assetCode: string | null, occupancy: number | null) => void
}> = ({ 
  modelPath, 
  occupancyData,
  flowData,
  thresholds = { green: 20, yellow: 25, amber: 30 },
  checkpointId,
  showPassengerFlow,
  onHover
}) => {
  const { scene: gltfScene } = useGLTF(modelPath);
  
  // Find heatmap objects from our exported scene
  const [heatmapObjects, setHeatmapObjects] = useState<THREE.Object3D[]>([]);
  
  useEffect(() => {
    if (!gltfScene) return;
    
    const heatmaps: THREE.Object3D[] = [];
    
    // Traverse the scene to find our heatmap polygons
    gltfScene.traverse((child) => {
      if (child.name.startsWith('Heatmap_t1-1a') || child.name.startsWith('Heatmap_t1-1b')) {
        heatmaps.push(child);
        
        // Apply default green material immediately
        if (child instanceof THREE.Mesh) {
          const defaultMaterial = new THREE.MeshStandardMaterial({
            color: new THREE.Color(0.3, 0.9, 0.3), // Light green
            transparent: true,
            opacity: 0.6,
            side: THREE.DoubleSide,
            emissive: new THREE.Color(0.3, 0.9, 0.3),
            emissiveIntensity: 0.1,
          });
          child.material = defaultMaterial;
        }
      }
      
      // Make floor black for better contrast
      if (child.name.includes('Floor') || child.name.includes('Checkpoint_Floor')) {
        if (child instanceof THREE.Mesh && child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              if (mat instanceof THREE.MeshStandardMaterial) {
                mat.color.setHex(0x111111); // Dark gray
              }
            });
          } else if (child.material instanceof THREE.MeshStandardMaterial) {
            child.material.color.setHex(0x111111); // Dark gray
          }
        }
      }
    });
    
    setHeatmapObjects(heatmaps);
    
  }, [gltfScene, checkpointId]);

  return (
    <group position={[0, 0, 0]}>
      <primitive object={gltfScene} />
      {heatmapObjects.map((heatmapObj, index) => (
        <HeatmapZone
          key={`heatmap-${index}`}
          object={heatmapObj}
          occupancyData={occupancyData}
          thresholds={thresholds}
          onHover={onHover}
        />
      ))}
      {/* Add Passenger Flow Animation */}
      <PassengerFlowAnimation
        heatmapObjects={heatmapObjects}
        occupancyData={occupancyData}
        flowData={flowData}
        thresholds={thresholds}
        enabled={showPassengerFlow}
      />
    </group>
  );
};

// Camera Controls Component with centered targeting for CP-1B
const CameraControls: React.FC<{ 
  checkpointId: string,
  preset: string
}> = ({ checkpointId, preset }) => {
  const { camera } = useThree();
  const controlsRef = useRef<any>();

  const cameraPresets = useMemo(() => {
    // Different presets for CP-1A and CP-1B
    if (checkpointId === 'CP-1B' || checkpointId === 'cp-1b') {
      // CP-1B is centered at origin
      return {
        overview: { position: [0, 50, 40], target: [0, 0, 0] },
        top: { position: [0, 80, 0], target: [0, 0, 0] },
        close: { position: [0, 25, 20], target: [0, 0, 0] },
        side: { position: [60, 30, 0], target: [0, 0, 0] },
        front: { position: [0, 30, 50], target: [0, 0, 0] },
        security: { position: [-40, 35, 25], target: [0, 0, 0] }
      };
    } else {
  // CP-1A - Model is at X: 0-73, Y: 0-37 (Z in Three.js)
  // Center is at approximately (36.5, 0, 18.5) in Three.js coordinates
  const centerX = 36.5;
  const centerZ = 18.5;
  return {
    overview: { position: [centerX, 60, centerZ + 10], target: [centerX, 0, centerZ] },  // Added Z offset
    top: { position: [centerX, 80, centerZ], target: [centerX, 0, centerZ] },
    close: { position: [centerX, 25, centerZ + 15], target: [centerX, 0, centerZ] },
    side: { position: [centerX + 40, 30, centerZ], target: [centerX, 0, centerZ] },
    front: { position: [centerX, 30, centerZ + 35], target: [centerX, 0, centerZ] },
    security: { position: [centerX - 30, 35, centerZ + 20], target: [centerX, 0, centerZ] }
  };
}
  }, [checkpointId]);

  useEffect(() => {
    const config = cameraPresets[preset as keyof typeof cameraPresets] || cameraPresets.overview;
    
    // Smoothly animate camera to new position
    camera.position.set(config.position[0], config.position[1], config.position[2]);
    camera.lookAt(config.target[0], config.target[1], config.target[2]);
    camera.updateProjectionMatrix();
  }, [preset, camera, cameraPresets]);

  const targetPosition = checkpointId === 'CP-1B' || checkpointId === 'cp-1b' 
    ? [0, 0, -10] 
    : [36.5, 0, -10];  // Reduced Z to shift view down in viewport

  return (
    <OrbitControls 
      ref={controlsRef}
      target={targetPosition as [number, number, number]}
      enablePan={true}
      enableZoom={true}
      enableRotate={true}
      minDistance={15}
      maxDistance={100}
      minPolarAngle={0.1}
      maxPolarAngle={Math.PI / 2.2}
      enableDamping={true}
      dampingFactor={0.05}
    />
  );
};
// Main component
export const Fresh3DVisualizationEngine: React.FC<Fresh3DEngineProps> = ({ 
  modelPath, 
  occupancyData,
  flowData,
  thresholds = { green: 20, yellow: 25, amber: 30 },
  checkpointId,
  className = ""
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPreset, setCurrentPreset] = useState('overview');
  const [showPassengerFlow, setShowPassengerFlow] = useState(true); // Enabled by default
  const [hoveredZone, setHoveredZone] = useState<{ assetCode: string; occupancy: number } | null>(null);
  
  // Handle hover events
  const handleHover = useCallback((assetCode: string | null, occupancy: number | null) => {
    if (assetCode && occupancy !== null) {
      setHoveredZone({ assetCode, occupancy });
    } else {
      setHoveredZone(null);
    }
  }, []);
  
  // Handle loading states
  const handleLoad = useCallback(() => {
    setIsLoading(false);
  }, []);
  
  const handleError = useCallback((error: any) => {
    setError(`Failed to load 3D model: ${error.message}`);
    setIsLoading(false);
  }, []);
  
  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-900 text-white ${className}`}>
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">3D Model Error</h3>
          <p className="text-gray-400">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 rounded hover:bg-blue-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
  
  // Determine initial camera position based on checkpoint
  const initialCameraPosition = checkpointId === 'CP-1B' || checkpointId === 'cp-1b'
    ? [0, 40, 30] as [number, number, number]
    : [36.5, 60, 28.5] as [number, number, number];  // Increased Z to move camera back
  
  return (
    <div className={`relative w-full h-full bg-gray-900 ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
            <p>Loading {checkpointId === 'cp-1a' ? 'CP-1A' : 'CP-1B'} Checkpoint Model...</p>
          </div>
        </div>
      )}
      
      {/* Hover Tooltip */}
      {hoveredZone && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-30 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl border border-gray-700">
          <div className="text-xs flex items-center space-x-1">
            <span className="text-gray-400">Occupancy:</span>
            <span className="font-bold text-white">{hoveredZone.occupancy}</span>
            <span className="text-gray-400">people</span>
          </div>
          {flowData && flowData[hoveredZone.assetCode] && (
            <div className="text-xs flex items-center space-x-1">
              <span className="text-gray-400">Outflow:</span>
              <span className="font-bold text-white">{flowData[hoveredZone.assetCode]}</span>
              <span className="text-gray-400">people/min</span>
            </div>
          )}
        </div>
      )}
      
      <Canvas
        style={{ 
          width: '100%', 
          height: '100vh',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
        }}
        camera={{ 
          fov: 50, 
          position: initialCameraPosition,
          near: 0.1,
          far: 500
        }}
        shadows
        onCreated={handleLoad}
        onError={handleError}
      >
        {/* Lighting */}
        <ambientLight intensity={0.4} />
        <directionalLight 
          position={[50, 50, 25]} 
          intensity={1.5}
          color="#ffffff"
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-camera-far={200}
          shadow-camera-left={-100}
          shadow-camera-right={100}
          shadow-camera-top={100}
          shadow-camera-bottom={-100}
        />
        <directionalLight position={[-25, 40, 30]} intensity={0.8} color="#f1f5f9" />
        
        {/* Environment with gradient background */}
        <Environment preset="warehouse" />
        
        <React.Suspense fallback={null}>
          <Scene3D
            modelPath={modelPath}
            occupancyData={occupancyData}
            flowData={flowData}
            thresholds={thresholds}
            checkpointId={checkpointId}
            showPassengerFlow={showPassengerFlow}
            onHover={handleHover}
          />
        </React.Suspense>
        
        {/* Camera controls */}
        <CameraControls checkpointId={checkpointId} preset={currentPreset} />
      </Canvas>
      
      {/* Desktop Camera Preset Buttons - Hidden on mobile */}
      <div className="absolute top-4 right-4 hidden lg:flex flex-col space-y-2 z-20">
        {['overview', 'top', 'close', 'side', 'front', 'security'].map((preset) => (
          <button
            key={preset}
            onClick={() => setCurrentPreset(preset)}
            className={`px-3 py-2 rounded text-sm font-medium transition-colors min-w-[100px] ${
              currentPreset === preset
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
            }`}
            title={`${preset.charAt(0).toUpperCase() + preset.slice(1)} view`}
          >
            {preset.charAt(0).toUpperCase() + preset.slice(1)}
          </button>
        ))}
        
        {/* Passenger Flow Toggle */}
        <div className="border-t border-gray-600 pt-2">
          <button
            onClick={() => setShowPassengerFlow(!showPassengerFlow)}
            className={`px-3 py-2 rounded text-sm font-medium transition-colors min-w-[100px] ${
              showPassengerFlow
                ? 'bg-green-600 text-white shadow-lg'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
            }`}
            title={showPassengerFlow ? 'Hide passenger flow' : 'Show passenger flow'}
          >
            {showPassengerFlow ? '✓ Flow On' : 'Flow Off'}
          </button>
        </div>
        
        {/* Reset button */}
        <button
          onClick={() => setCurrentPreset('overview')}
          className="px-3 py-2 rounded text-xs font-medium bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300 border border-gray-600"
          title="Reset to default view"
        >
          Reset View
        </button>
      </div>

      {/* Mobile Controls - Visible only on mobile */}
      <div className="lg:hidden">
        {/* Mobile floating action buttons */}
        <div className="absolute bottom-4 right-4 flex flex-col space-y-2 z-20">
          {/* Quick View Toggle */}
          <button
            onClick={() => {
              // Cycle through main views on mobile
              const mobilePresets = ['overview', 'top', 'close'];
              const currentIndex = mobilePresets.indexOf(currentPreset);
              const nextIndex = (currentIndex + 1) % mobilePresets.length;
              setCurrentPreset(mobilePresets[nextIndex]);
            }}
            className="bg-gray-800 text-white p-3 rounded-full shadow-lg border border-gray-600"
            title="Change view"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>

          {/* Flow Toggle */}
          <button
            onClick={() => setShowPassengerFlow(!showPassengerFlow)}
            className={`p-3 rounded-full shadow-lg ${
              showPassengerFlow
                ? 'bg-green-600 text-white'
                : 'bg-gray-800 text-gray-400 border border-gray-600'
            }`}
            title={showPassengerFlow ? 'Hide flow' : 'Show flow'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </button>

          {/* Reset View */}
          <button
            onClick={() => setCurrentPreset('overview')}
            className="bg-gray-800 text-gray-400 p-3 rounded-full shadow-lg border border-gray-600"
            title="Reset view"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Mobile view indicator - shows current view */}
        <div className="absolute top-4 left-4 z-20">
          <div className="bg-gray-800 text-white px-3 py-1 rounded-lg text-sm border border-gray-600">
            {currentPreset === 'overview' && 'Overview'}
            {currentPreset === 'top' && 'Top View'}
            {currentPreset === 'close' && 'Close Up'}
            {currentPreset === 'side' && 'Side View'}
            {currentPreset === 'front' && 'Front View'}
            {currentPreset === 'security' && 'Security'}
          </div>
        </div>
      </div>
      
      {/* Status indicator */}
      {/* {occupancyData && (
        <div className="absolute bottom-4 left-4 bg-gray-800 px-3 py-2 rounded text-sm text-gray-300">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Live Data ({Object.keys(occupancyData).length} zones)</span>
          </div>
        </div>
      )} */}
    </div>
  );
};